import { Card, Skeleton, Text, useDisclosure } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { FundraisingType, routes } from '@blockpulse3/data/shared';
import {
  DocumentStatus,
  FundraisingDocumentType,
  FundraisingStatus,
  useGetBankAccountByCompanyIdQuery,
  useGetCompanyQuery,
  useGetFundraisingQuery,
  usePreviewFundraisingDocumentLazyQuery,
  useValidateFundraisingDocumentMutation,
} from '@blockpulse3/graphql/hooks';
import { b64toBlob } from '@blockpulse3/helpers';
import {
  ErrorQueryCard,
  CONTENT_ERROR as PDF_URL_ERROR,
  CONTENT_LOADING as PDF_URL_LOADING,
  Progression,
  ProgressionStep,
  SignDocumentModal,
  SignatureDocument,
  useErrorToast,
  useSuccessToast,
} from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { getFundraisingDocuments, getFundraisingProgressionStep } from './utils';

/**
 * FundraisingProgression.
 * Progression section with sign document CTAs of the Fundraising.
 *
 * @returns {JSX.Element}
 */
export function FundraisingProgression(): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nDocumentValues = useTranslations('DocumentValues');
  const i18nFundraisingProgression = useTranslations('FundraisingProgression');

  const { fundraisingId = '', companyId = '' } = useParams();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { user } = useUser();

  const companyReq = useGetCompanyQuery({
    variables: { companyId },
    skip: !companyId,
  });
  const company = companyReq?.data?.company;

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [documentType, setDocumentType] = useState<FundraisingDocumentType | null>(null);
  const [documentStatus, setDocumentStatus] = useState<DocumentStatus | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<FundraisingDocumentType | null>(null);

  /* ** Cache fundraising document previews. TODO: remove unused documents ** */
  const [documentPreviews, setDocPrev] = useState<Record<FundraisingDocumentType, string | symbol>>(
    {
      [FundraisingDocumentType.PV_EXERCICE]: PDF_URL_LOADING,
      [FundraisingDocumentType.SUBSCRIPTION]: PDF_URL_LOADING,
      [FundraisingDocumentType.PV_CONSTATATION]: PDF_URL_LOADING,
      [FundraisingDocumentType.IBAN]: PDF_URL_LOADING,
      [FundraisingDocumentType.SHAREHOLDER_AGREEMENT]: PDF_URL_LOADING,
      [FundraisingDocumentType.OWNERSHIP_CERTIFICATE]: PDF_URL_LOADING,
      [FundraisingDocumentType.PEA_PME_CERTIFICATE]: PDF_URL_LOADING,
      [FundraisingDocumentType.PEA_CERTIFICATE]: PDF_URL_LOADING,
      [FundraisingDocumentType.IR_PME_CERTIFICATE]: PDF_URL_LOADING,
      [FundraisingDocumentType.CUSTOM_FILE]: PDF_URL_LOADING,
      [FundraisingDocumentType.FUNDS_SOURCE_CERTIFICATE]: PDF_URL_LOADING,
      [FundraisingDocumentType.MIFID]: PDF_URL_LOADING,
      [FundraisingDocumentType.RMT_EXTRACT]: PDF_URL_LOADING,
      [FundraisingDocumentType.DEPOSIT_CERTIFICATE]: PDF_URL_LOADING,
    },
  );

  const [validateFundraisingDocument, { loading: isDocumentValidating }] =
    useValidateFundraisingDocumentMutation();

  const [loadPreview] = usePreviewFundraisingDocumentLazyQuery();

  const { data, loading, error, refetch } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
  });

  const { data: bankAccount, loading: loadingBankAccount } = useGetBankAccountByCompanyIdQuery({
    variables: {
      companyId: data?.fundraising.company.id || '',
    },
    skip: !data?.fundraising || data?.fundraising?.isExternalIBAN,
  });

  if (!fundraisingId || loading || loadingBankAccount) {
    return (
      <Skeleton>
        <Card h="180px" />
      </Skeleton>
    );
  }

  if (error) {
    return <ErrorQueryCard h="180px" />;
  }

  if (!data) {
    return <ErrorQueryCard h="180px" />;
  }

  const fundraising = data.fundraising;
  const documents = fundraising.documents || [];

  /* ** Is current user a signer and able to be redirected to SignatureIt ** */
  const isUserSigner = company?.signer?.individualIdentity?.id === user?.individualIdentity?.id;

  const fundraisingRoutes =
    fundraising.type === FundraisingType.CROWDFUNDING
      ? routes.workspace.fundraising.crowdfunding
      : routes.workspace.fundraising.private;

  const url = selectedDocument ? documentPreviews[selectedDocument] : PDF_URL_LOADING;
  const isEditable = fundraising.status === FundraisingStatus.DRAFT;
  const currentStep = getFundraisingProgressionStep(fundraising.status);
  const fundraisingDocuments = getFundraisingDocuments(documents);

  const handlePreviewModalClose = (): void => {
    onClose();
  };

  const handleDocumentSign = (): void => {
    if (!documentType) return;
    if (fundraising) {
      validateFundraisingDocument({
        variables: {
          validateFundraisingDocumentInput: {
            fundraisingId: fundraising?.id,
            documentType,
          },
        },
        onCompleted: (data) => {
          if (data.validateFundraisingDocument?.signer?.signingUrl) {
            window.open(data.validateFundraisingDocument.signer.signingUrl, '_self');
          } else {
            successToast({ title: i18nFundraisingProgression('Toast.SignatureSentSuccess') });
            setIsDisabled(true);
          }
          refetch();
        },
        onError: () => {
          errorToast({ title: i18nFundraisingProgression('Toast.SignatureSentError') });
        },
      });
    }
  };

  const handleDocumentClick = (type: FundraisingDocumentType): void => {
    const title = ('Document' + type) as keyof IntlMessages['DocumentValues'];

    const fundraisingDocument = fundraisingDocuments[type];

    setDocumentType(type);
    setSelectedDocument(type);
    setModalTitle(i18nDocumentValues(title));

    if (fundraisingDocument) {
      const { lastRemindEmailSentAt, createdAt } = fundraisingDocument.document.signature || {};
      const shouldRemind = dayjs(lastRemindEmailSentAt || createdAt).isBefore(
        dayjs().subtract(1, 'h'),
      );
      setIsDisabled(!shouldRemind);
      setDocumentStatus(fundraisingDocument.document.status);
    } else {
      setIsDisabled(false);
      setDocumentStatus(null);
    }

    onOpen();

    if (typeof documentPreviews[type] !== 'string') {
      loadPreviewURL(type);
    }
  };

  const loadPreviewURL = (type: FundraisingDocumentType): void => {
    if (fundraising) {
      loadPreview({
        variables: {
          documentType: type,
          previewFundraisingDocumentId: fundraising.id,
        },
        onCompleted({ pdfURL }: { pdfURL: string }) {
          let objURL: string;
          if (pdfURL.startsWith('http')) {
            objURL = pdfURL;
          } else {
            const blob = b64toBlob(pdfURL, 'application/pdf');
            objURL = URL.createObjectURL(blob);
          }

          setDocPrev((curr) => ({ ...curr, [type]: objURL }));
        },

        onError() {
          setDocPrev((curr) => ({
            ...curr,
            [type]: PDF_URL_ERROR,
          }));
        },
      });
    }
  };

  const canSignPV = data?.fundraising.isExternalIBAN || bankAccount;
  const canSignConstatation =
    currentStep >= 3 && !!fundraisingDocuments[FundraisingDocumentType.PV_CONSTATATION]?.document;

  return (
    <Card>
      <Progression currentStep={currentStep} totalSteps={4}>
        <ProgressionStep isCompleted title={i18nFundraisingProgression('Step1')}>
          {isEditable && (
            <Link to={fundraisingRoutes.parameters.fmt(companyId || '', fundraising?.id || '')}>
              <Text fontSize="xs" fontWeight="semibold" textDecoration="underline">
                {i18nGeneral('Modify')}
              </Text>
            </Link>
          )}
        </ProgressionStep>
        <ProgressionStep isCompleted={currentStep > 1} title={i18nFundraisingProgression('Step2')}>
          <Text color="gray.600" fontSize="xs">
            {i18nFundraisingProgression.rich('SubTitle2', {
              important: (chunks) => <b>{chunks}</b>,
            })}
          </Text>
          <SignatureDocument
            data-cy="sign-president-exercise"
            isDisabled={!canSignPV}
            size="sm"
            status={fundraisingDocuments[FundraisingDocumentType.PV_EXERCICE]?.document?.status}
            title={i18nFundraisingProgression('PresidentExercisePV')}
            onClick={(): void => handleDocumentClick(FundraisingDocumentType.PV_EXERCICE)}
          />
        </ProgressionStep>
        <ProgressionStep isCompleted={currentStep > 2} title={i18nFundraisingProgression('Step3')}>
          <Text color="gray.600" fontSize="xs">
            {i18nFundraisingProgression('SubTitle3')}
          </Text>
        </ProgressionStep>
        <ProgressionStep isCompleted={currentStep > 3} title={i18nFundraisingProgression('Step4')}>
          <Text color="gray.600" fontSize="xs">
            {i18nFundraisingProgression.rich('SubTitle4', {
              important: (chunks) => <b>{chunks}</b>,
            })}
          </Text>
          <SignatureDocument
            isDisabled={!canSignConstatation}
            size="sm"
            status={fundraisingDocuments[FundraisingDocumentType.PV_CONSTATATION]?.document?.status}
            title={i18nFundraisingProgression('ObservationPV')}
            onClick={(): void => handleDocumentClick(FundraisingDocumentType.PV_CONSTATATION)}
          />
          <SignDocumentModal
            isDisabled={isDisabled}
            isOpen={isOpen}
            isSignatureLoading={isDocumentValidating}
            isUserSigner={isUserSigner}
            src={url}
            status={documentStatus}
            title={modalTitle}
            onCancel={handlePreviewModalClose}
            onClose={handlePreviewModalClose}
            onSign={handleDocumentSign}
          />
        </ProgressionStep>
      </Progression>
    </Card>
  );
}
